import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { Card, Dropzone, Flex, Icon, Text, showErrorToast } from '@amigoapp/doca-react';
import { useDropzone } from 'react-dropzone';
import { ACCEPTED_FILE_DROPZONE_TYPES, MAX_PATIENT_DOCUMENT_FILE } from '../../constants/patientFiles';
import { getPatientFileKey } from '../../utils/patientFiles';
import {
  FileListItem,
  FileTypeIconContainer,
  UploadIconContainer,
} from './FileUpload.styles';

const UPLOAD_BOX_HEIGHT = 68;

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

const getFileCategory = (file) => {
  const mimeType = file?.type;

  if (mimeType?.startsWith('image/')) {
    return 'image';
  }

  if (mimeType === 'application/pdf') {
    return 'document';
  }

  const fileName = file?.name || file?.url || '';
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (IMAGE_EXTENSIONS.includes(extension)) {
    return 'image';
  }

  if (extension === 'pdf') {
    return 'document';
  }

  return null;
};

const getIconByFileType = (type) => {
  if (type === 'image') {
    return 'doca-icon__image';
  }

  if (type === 'document') {
    return 'doca-icon__file-pdf';
  }

  return '';
};

const getFileLabel = (file) => file?.name || file?.url || 'Arquivo anexado';
const formatFileSize = (bytes) => {

  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }

  return `${(kb / 1024).toFixed(1)} MB`;
};

const FileList = ({ files, onRemoveFile, compact = false, cardStyle = false }) => {
  if (!files?.length) {
    return null;
  }

  return (
    <Flex
      direction="col"
      className={compact ? '' : 'doca-mt-2'}
    >
      {files.map((file, index) => {
          const fileKey = getPatientFileKey(file, index);
          const fileCategory = getFileCategory(file);
          const fileSize = formatFileSize(file?.size);

          return (
            <Flex
              key={`${fileKey}-${index}`}
              align="center"
              className={`doca-px-3 ${compact ? 'doca-h-full doca-border-0 doca-rounded-none' : 'doca-py-2 doca-mb-2'} ${cardStyle ? 'doca-pr-4' : ''}`}
              as={FileListItem}
              compact={compact}
            >
              {cardStyle ? (
                <FileTypeIconContainer className="doca-flex doca-items-center doca-justify-center doca-flex-none doca-mr-3">
                  <i className={`doca-icon ${getIconByFileType(fileCategory) || 'doca-icon__file'}`} />
                </FileTypeIconContainer>
              ) : (
                <i className={`doca-icon ${getIconByFileType(fileCategory) || 'doca-icon__file'} doca-mr-2 doca-flex-none`} />
              )}
              <Flex direction="col" className="doca-flex-1 doca-min-w-0">
                <Text className="doca-truncate">
                  {getFileLabel(file)}
                </Text>
                {fileSize && (
                  <Text size="small" className="doca-text--gray doca-mt-0">
                    {fileSize}
                  </Text>
                )}
              </Flex>
              {onRemoveFile && (
                <Icon
                  name="xmark"
                  className="doca-cursor-pointer doca-flex-none"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemoveFile(fileKey);
                  }}
                />
              )}
            </Flex>
          );
        })}
    </Flex>
  );
};

const getUploadIconBackgroundClass = (colorVariant, isUploadDisabled) => {
  if (isUploadDisabled) {
    return 'doca-bg-light-low';
  }

  return `doca-bg-${colorVariant || 'primary'}-low`;
};

const CardFileUpload = forwardRef(({
  uploadLabel,
  maxFiles,
  onFileSelect,
  loading,
  selectedFiles,
  colorVariant,
  draggingLabel = 'Solte aqui',
}, ref) => {
  const onDropAccepted = useCallback((files) => {
    onFileSelect?.({ target: { value: files } });
  }, [onFileSelect]);

  const onDropRejected = useCallback((fileRejections) => {
    const hasInvalidType = fileRejections.some(({ errors }) =>
      errors.some((error) => error.code === 'file-invalid-type')
    );

    if (hasInvalidType) {
      showErrorToast('Formato de arquivo não suportado. Envie apenas JPG, PNG, WEBP ou PDF.');
    }
  }, []);

  const selectedFilesCount = selectedFiles?.length || 0;
  const isLimitReached = selectedFilesCount >= maxFiles;
  const isUploadDisabled = loading || isLimitReached;

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: ACCEPTED_FILE_DROPZONE_TYPES,
    multiple: true,
    disabled: isUploadDisabled,
  });

  useImperativeHandle(ref, () => ({ open }), [open]);

  const displayLabel = isDragActive ? draggingLabel : uploadLabel;
  const { className: dropzoneClassName, style: dropzoneStyle, ...rootProps } = getRootProps();

  const limitTooltip = isLimitReached
    ? `Você atingiu o limite de ${maxFiles} ${maxFiles === MAX_PATIENT_DOCUMENT_FILE ? 'arquivo permitido' : 'arquivos permitidos'} para anexar.`
    : undefined;

  return (
    <div className="doca-w-full" title={limitTooltip}>
      <Card
        {...rootProps}
        className={`doca-flex doca-w-full doca-overflow-hidden ${isUploadDisabled ? '' : 'doca-cursor-pointer'} ${isDragActive ? 'doca-card--info' : ''} ${isUploadDisabled ? 'doca-opacity-50' : ''} ${dropzoneClassName || ''}`}
        style={{
          height: UPLOAD_BOX_HEIGHT,
          minHeight: UPLOAD_BOX_HEIGHT,
          ...dropzoneStyle,
          pointerEvents: isUploadDisabled ? 'none' : undefined,
        }}
      >
        <input {...getInputProps()} />
        <Flex align="center" className="doca-w-full doca-h-full doca-truncate">
          <UploadIconContainer
            className={`doca-flex doca-items-center doca-justify-center doca-h-full doca-w-16 doca-flex-none doca-py-0 ${getUploadIconBackgroundClass(colorVariant, isUploadDisabled)}`}
          >
            <Icon
              name="plus"
              size="large"
              className={isUploadDisabled ? 'doca-text--gray' : ''}
            />
          </UploadIconContainer>
          <Card.Content className="doca-py-0 doca-px-4 doca-flex doca-items-center doca-flex-1 doca-min-w-0 doca-truncate">
            <Card.Title className={`doca-truncate doca-text--normal ${isUploadDisabled ? 'doca-text--gray' : ''}`}>
              {displayLabel}
            </Card.Title>
          </Card.Content>
        </Flex>
      </Card>
    </div>
  );
});

CardFileUpload.displayName = 'CardFileUpload';

const FileUpload = forwardRef(({
  onFileSelect,
  maxFiles = 4,
  defaultFile,
  selectedFiles,
  loading,
  onRemoveFile,
  label,
  variant = 'default',
  colorVariant,
}, ref) => {
  const hasCustomList = Array.isArray(selectedFiles);
  const isCardVariant = variant === 'card';

  return (
    <Flex direction="col" className="doca-w-full">
      {isCardVariant ? (
        <CardFileUpload
          ref={ref}
          uploadLabel={label}
          maxFiles={maxFiles}
          onFileSelect={onFileSelect}
          loading={loading}
          selectedFiles={selectedFiles}
          onRemoveFile={onRemoveFile}
          colorVariant={colorVariant}
        />
      ) : (
        <Dropzone
          ref={ref}
          defaultValue={defaultFile}
          draggingLabel="Solte aqui"
          label={label || 'Arraste ou clique para anexar arquivos'}
          maxFiles={maxFiles}
          onChange={onFileSelect}
          showSelectedFiles={!hasCustomList}
        />
      )}

      {hasCustomList && (
        <FileList
          files={selectedFiles}
          onRemoveFile={onRemoveFile}
          cardStyle={isCardVariant}
        />
      )}
    </Flex>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;
